import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ICDiagnosticsService } from './ic-diagnostics.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-ic-diagnostics',
    templateUrl: './ic-diagnostics.component.html',
    styleUrls: ['./ic-diagnostics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ICDiagnosticsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Entity list for display
  entityList: any[] = [];

  // Component properties from AngularJS controller
  modalTitle = 'Diagnostic: Intercompany Outside Own Business';
  userSettings: any;
  logged_in_user: string = '';
  displayName: string = '';
  entitySelected: any = {};
  col_id: string = '';
  cpFormData: any[] = [];
  cpFormOriginalData: any[] = [];
  cpFormFinalData: any[] = [];
  finalSaveData: any[] = [];
  clob_settings: any[] = [];
  
  entityTbAmt: number = 0;
  allocate_pending_amt: number = 0;
  pending_amt: number = 0;
  variance: number = 0;

  editMode: boolean = false;
  editMe: boolean = false;
  
  icoAccountList: any[] = [];
  selectedLine_no: string = '';
  entityDetails: any[] = [];
  selectedEntity: string = '';
  
  searchBy: string = 'L';
  applicable_year: string = '';
  payorDetails: any[] = [];
  componentDetails: any[] = [];
  componentSelected: any = {};
  currentLine: string = '';
  edit_record_index: number | null = null;
  edit_record_parentindex: number | null = null;
  edit_record_occurrence: any = null;
  dummyApprovedRecord: any = {
    "ROW_STATUS": "A",
    "ROW_TYPE": "CH",
    "ROW_SOURCE": "R",
    "DELETE_ROWS": [],
    "Line_data": []
  };
  
  combination_key: string = '';
  selectedBSLA: string = '';
  selectedMECode: string = '';
  allocated_amount: number = 0;
  total_net_amt: number = 0;
  total_amt_allocated: number = 0;
  cur_variance: number = 0;
  total_allocated_amt: number = 0;

  form_name: string = 'IC_DIAG';
  schedule_name: string = 'IC_OUTSIDE_BU';
  
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // Data loading optimization
  private dataLoadingStates = {
    entityData: false,
    icoAccountData: false,
    componentData: false,
    cpFormData: false
  };

  // Initialization guard to prevent multiple API calls
  private isInitialized = false;
  private hasAllRequiredInputs = false;

  constructor(
    private icDiagnosticsService: ICDiagnosticsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit() {
    this.applyWorkspaceContext();
    this.checkAndInitialize();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.checkAndInitialize();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.checkAndInitialize();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  private checkAndInitialize() {
    if (this.isInitialized) {
      return;
    }

    const hasBaseUrl = this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url && this.GENERAL_CONFIG.base_url !== '';
    const hasHeaderData = this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0;
    const hasRowData = this.rowData && Object.keys(this.rowData).length > 0 && this.rowData.COMBINATION_KEY;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasUserSettings = this.USER_SETTINGS && this.USER_SETTINGS.user;

    if (hasBaseUrl && hasHeaderData && hasRowData && hasFilterParams && hasUserSettings) {
      this.isInitialized = true;
      this.init();
    }
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.getAllVariableDetails();
      }
    } catch (e) {
      console.error('Error in IC-Diagnostics init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // Use REAL data from rowData
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.CDR_NO;
    this.leType = this.rowData.LE_TYPE;
    
    if (this.USER_SETTINGS) {
      this.userSettings = this.USER_SETTINGS;
      this.logged_in_user = this.userSettings.user?.sso_id || '';
      this.displayName = this.userSettings.user?.display_name || '';
    }
    
    // Set entity selected from rowData
    this.entitySelected = this.rowData;
    this.applicable_year = this.rowData.TAX_YEAR;
    this.currentLine = this.rowData.LINE_NO;
    
    // MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
    
    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  getAllVariableDetails() {
    this.combination_key = this.rowData.COMBINATION_KEY;
    
    // Load component details and ICO account data
    this.getComponentDetails();
    this.getICOAcctData();
  }

  getICOAcctData() {
    this.icDiagnosticsService.getICOutsideAcctDetails(
      this.GENERAL_CONFIG.base_url,
      this.combination_key,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.icoAccountList = data.jsonObject || [];
        this.getTotalNetAmt();
        this.getCPFormData();
        this.dataLoadingStates.icoAccountData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading ICO Account data:', error);
        this.dataLoadingStates.icoAccountData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      }
    );
  }

  getTotalNetAmt() {
    const total_net_index = this.icoAccountList.findIndex(x => x.ROW_TYPE === 'TOTAL_ROW');
    if (total_net_index !== -1) {
      const total_component = this.icoAccountList[total_net_index];
      this.total_net_amt = total_component.NET_AMT;
    }
  }

  getComponentDetails() {
    this.icDiagnosticsService.getBSLADetails(
      this.GENERAL_CONFIG.base_url,
      this.logged_in_user,
      this.combination_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.componentDetails = data.jsonObject || [];
        this.dataLoadingStates.componentData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading component details:', error);
        this.dataLoadingStates.componentData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      }
    );
  }

  afterBSLASelected() {
    const selectedbslaIndex = this.componentDetails.findIndex(x => x.BSLA_DETAILS === this.selectedBSLA);
    if (selectedbslaIndex !== -1) {
      this.componentSelected = this.componentDetails[selectedbslaIndex];
    }
  }

  getCPFormData() {
    this.icDiagnosticsService.getCPFormDetails(
      this.GENERAL_CONFIG.base_url,
      this.combination_key,
      this.form_name,
      this.schedule_name,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const cpList = data.jsonObject || [];
        
        // Group by GROUP_ORDER
        const grouped = cpList.reduce((a: any, b: any) => {
          a[b.GROUP_ORDER] = a[b.GROUP_ORDER] || [];
          a[b.GROUP_ORDER].push(b);
          return a;
        }, {});
        
        const grouped_array = Object.keys(grouped).map((k: string) => {
          return {
            ROW_STATUS: grouped[k][0].PAYOR_STATUS,
            ROW_TYPE: grouped[k][0].ROW_TYPE,
            DELETE_ROWS: [],
            Line_data: grouped[k]
          };
        });
        
        this.cpFormData = _.cloneDeep(grouped_array);
        
        // Process the form data
        this.processFormData();
        
        this.dataLoadingStates.cpFormData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading CP Form data:', error);
        this.dataLoadingStates.cpFormData = true;
        this.checkAllDataLoaded();
        this.cdr.detectChanges();
      }
    );
  }

  processFormData() {
    for (let i = 0; i < this.cpFormData.length; i++) {
      if (this.cpFormData[i].ROW_TYPE === 'CH') {
        
        if (this.cpFormData[i].Line_data[0].OCCURRENCE != null) {
          // Group by OCCURRENCE
          const z = this.cpFormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
            a[b.OCCURRENCE].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "DATA_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Lock_status: z[k][0].LOCK_STATUS,
              Line_no: z[k][0].LINE_NO,
              IS_EDITABLE: z[k][0].IS_EDITABLE,
              IS_EDITTED: "N",
              Trans_details_key: z[k][0].TRANS_DETAILS_KEY,
              Occurrence: z[k][0].OCCURRENCE,
              Line_data_occ: z[k]
            };
          });
          
          // Process display text for ALLOCATED_BU
          for (let n = 0; n < y.length; n++) {
            if (y[n].Line_Type === 'DATA_ROW') {
              for (let j = 0; j < y[n].Line_data_occ.length; j++) {
                if (y[n].Line_data_occ[j].ATTRIB_SHORT_CODE === 'ALLOCATED_BU') {
                  const bslaIndex = this.componentDetails.findIndex(x => x.ME_KEY == y[n].Line_data_occ[j].ATTRIB_VALUE);
                  if (bslaIndex != null && bslaIndex !== undefined && bslaIndex >= 0) {
                    y[n].Line_data_occ[j].DISPLAY_TEXT = this.componentDetails[bslaIndex].BSLA_DETAILS;
                  }
                }
              }
            }
          }
          
          const m = _.cloneDeep(y);
          const c: any = _.cloneDeep(y[0]);
          let tab_header = '';
          const tab_length = c.Line_data_occ.length;
          let tab_color = '';
          tab_header = 'Amount allocated to BU(s)';
          tab_color = 'green';
          
          c.Line_Type = "HEADER_ROW";
          c.Lock_status = 0;
          c.Occurrence = "";
          c.Trans_details_key = "";
          c.Payor_Type = 'O';
          c.Payor_Status = 'P';
          c.IS_EDITABLE = "N";
          for (let j = 0; j < c.Line_data_occ.length; j++) {
            c.Line_data_occ[j].ATTRIB_VALUE = c.Line_data_occ[j].ATTRIB_DESCRIPTION;
            c.Line_data_occ[j].OCCURRENCE = 0;
            c.Line_data_occ[j].TRANS_DETAILS_KEY = 0;
            c.Line_data_occ[j].PARENT_DETAIL_KEY = null;
            c.Line_data_occ[j].REP_TRANS_ID = null;
            c.Line_data_occ[j].TRANS_ID = null;
          }
          
          (y as any[]).unshift({Line_Type: "TABLE_HEADER", Line_Desc: tab_header, colLength: tab_length, Line_color: tab_color}, c);
          this.cpFormData[i].Line_data = _.cloneDeep(y);
          
        } else {
          // Group by LINE_NO
          const z = this.cpFormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.LINE_NO] = a[b.LINE_NO] || [];
            a[b.LINE_NO].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "HEADER_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Lock_status: z[k][0].LOCK_STATUS,
              Line_no: z[k][0].LINE_NO,
              IS_EDITABLE: z[k][0].IS_EDITABLE,
              IS_EDITTED: "N",
              Trans_details_key: "",
              Occurrence: "",
              Line_data_occ: z[k]
            };
          });
          
          for (let j = 0; j < y[0].Line_data_occ.length; j++) {
            y[0].Line_data_occ[j].ATTRIB_VALUE = y[0].Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          let tab_header = '';
          const tab_length = y[0].Line_data_occ.length;
          let tab_color = '';
          tab_header = 'Amount allocated to BU(s)';
          tab_color = 'green';
          
          (y as any[]).unshift({Line_Type: "TABLE_HEADER", Line_Desc: tab_header, colLength: tab_length, Line_color: tab_color});
          this.cpFormData[i].Line_data = y;
        }
      } else if (this.cpFormData[i].ROW_TYPE === 'TH') {
        if (this.cpFormData[i].Line_data[0].ATTRIB_SHORT_CODE === 'TOTAL_ALLOC_AMT_LBL') {
          this.cpFormData[i].Line_data[1].ATTRIB_VALUE = this.total_allocated_amt;
        }
      }
    }
    
    this.putTotalLineAmounts();
    this.cpFormOriginalData = _.cloneDeep(this.cpFormData);
  }

  getRequestedAmount(index: number): number {
    let amount = 0;
    for (let y = 0; y < this.cpFormData[index].Line_data.length; y++) {
      if (this.cpFormData[index].Line_data[y].Line_Type === 'DATA_ROW') {
        for (let z = 0; z < this.cpFormData[index].Line_data[y].Line_data_occ.length; z++) {
          if (this.cpFormData[index].Line_data[y].Line_data_occ[z].ATTRIB_SHORT_CODE === 'ALLOCATED_AMT') {
            amount = amount + parseFloat(this.cpFormData[index].Line_data[y].Line_data_occ[z].ATTRIB_VALUE);
          }
        }
      }
    }
    return amount;
  }

  getCalculatedAmt() {
    for (let i = 0; i < this.cpFormData.length; i++) {
      if (this.cpFormData[i].ROW_TYPE === 'CH') {
        if (this.getRequestedAmount(i) != 0) {
          this.total_allocated_amt = this.total_allocated_amt + this.getRequestedAmount(i);
        }
      }
    }
    if (this.total_net_amt === undefined) {
      this.total_net_amt = 0;
    }
    this.variance = this.total_net_amt - this.total_allocated_amt;
  }

  putTotalLineAmounts() {
    this.total_allocated_amt = 0;
    this.variance = 0;
    this.getCalculatedAmt();
    for (let i = 0; i < this.cpFormData.length; i++) {
      if (this.cpFormData[i].ROW_TYPE === 'TH') {
        if (this.cpFormData[i].Line_data[0].ATTRIB_SHORT_CODE === 'TOTAL_ALLOC_AMT_LBL') {
          this.cpFormData[i].Line_data[1].ATTRIB_VALUE = this.total_allocated_amt;
        } else if (this.cpFormData[i].Line_data[0].ATTRIB_SHORT_CODE === 'VARIANCE_LBL') {
          this.cpFormData[i].Line_data[1].ATTRIB_VALUE = this.variance;
        }
      }
    }
  }

  cleanFormData() {
    this.searchBy = 'L';
    this.selectedBSLA = '';
    this.allocated_amount = 0;
    this.applicable_year = this.rowData.TAX_YEAR;
  }

  reset() {
    this.cleanFormData();
    this.editMode = false;
    this.editMe = false;
    this.cdr.detectChanges();
  }

  changeStatus(Occurrence: any, Trans_details_key: any, index: number, parentIndex: number, action_param: string) {
    if (action_param === 'R') {
      this.cpFormData[parentIndex].Line_data[index].Payor_Status = 'R';
      for (let i = 0; i < this.cpFormData[parentIndex].Line_data[index].Line_data_occ.length; i++) {
        this.cpFormData[parentIndex].Line_data[index].Line_data_occ[i].PAYOR_STATUS = 'R';
      }
      this.cpFormData[parentIndex].DELETE_ROWS.push(this.cpFormData[parentIndex].Line_data[index]);
      this.cpFormData[parentIndex].Line_data.splice(index, 1);
    } else if (action_param === 'A') {
      this.cpFormData[parentIndex].Line_data[index].Payor_Status = 'A';
      const approvedTableIndex = this.cpFormData.findIndex(x => x.ROW_STATUS === 'A');
      if (approvedTableIndex != null && approvedTableIndex !== undefined && approvedTableIndex >= 0) {
        if (this.cpFormData[approvedTableIndex].ROW_TYPE === 'CH' && this.cpFormData[approvedTableIndex].ROW_STATUS === 'A') {
          this.cpFormData[approvedTableIndex].Line_data.push(this.cpFormData[parentIndex].Line_data[index]);
        }
      } else {
        const tab_header = 'Amount allocated and reconciled with counter party.';
        const tab_length = this.cpFormData[parentIndex].Line_data[1].Line_data_occ.length;
        this.dummyApprovedRecord.Line_data.push({Line_Type: "TABLE_HEADER", Line_Desc: tab_header, colLength: tab_length});
        this.dummyApprovedRecord.Line_data.push(this.cpFormData[parentIndex].Line_data[1]);
        this.dummyApprovedRecord.Line_data.push(this.cpFormData[parentIndex].Line_data[index]);
        this.cpFormData.unshift(this.dummyApprovedRecord);
        this.cpFormData[parentIndex + 1].Line_data.splice(index, 1);
      }
    }
    this.putTotalLineAmounts();
    this.cdr.detectChanges();
  }

  submit() {
    const occArray: number[] = [];
    let occurrance = 1;
    
    for (let m = 0; m < this.cpFormData.length; m++) {
      if (this.cpFormData[m].ROW_TYPE === 'CH') {
        if (null != this.cpFormData[m].Line_data[this.cpFormData[m].Line_data.length - 1].Occurrence &&
            this.cpFormData[m].Line_data[this.cpFormData[m].Line_data.length - 1].Occurrence !== "" &&
            this.cpFormData[m].Line_data[this.cpFormData[m].Line_data.length - 1].Occurrence !== undefined) {
          occArray.push(this.cpFormData[m].Line_data[this.cpFormData[m].Line_data.length - 1].Occurrence + 1);
        } else {
          occArray.push(1);
        }
      }
    }
    occurrance = Math.max.apply(null, occArray);
    
    if (this.editMode === true) {
      for (let i = 0; i < this.cpFormData[this.edit_record_parentindex!].Line_data.length; i++) {
        if (this.edit_record_index === i && this.cpFormData[this.edit_record_parentindex!].Line_data[i].Occurrence === this.edit_record_occurrence) {
          for (let j = 0; j < this.cpFormData[this.edit_record_parentindex!].Line_data[i].Line_data_occ.length; j++) {
            if (this.cpFormData[this.edit_record_parentindex!].Line_data[i].Line_data_occ[j].ATTRIB_SHORT_CODE === 'ALLOCATED_AMT') {
              this.cpFormData[this.edit_record_parentindex!].Line_data[i].Line_data_occ[j].ATTRIB_VALUE = this.allocated_amount;
              this.cpFormData[this.edit_record_parentindex!].Line_data[i].Line_data_occ[j].IS_CHANGED_FLAG = "Y";
            }
          }
        }
      }
    } else {
      for (let x = 0; x < this.cpFormData.length; x++) {
        if (this.cpFormData[x].ROW_TYPE === 'CH') {
          const new_row = _.cloneDeep(this.cpFormData[x].Line_data[1]);
          new_row.Line_Type = "DATA_ROW";
          new_row.Occurrence = occurrance;
          new_row.IS_EDITTED = "Y";
          new_row.Is_Multi = "Y";
          new_row.Lock_status = 0;
          for (let i = 0; i < new_row.Line_data_occ.length; i++) {
            if (new_row.Line_data_occ[i].ATTRIB_SHORT_CODE === 'ALLOCATED_AMT') {
              new_row.Line_data_occ[i].ATTRIB_VALUE = this.allocated_amount;
            } else if (new_row.Line_data_occ[i].ATTRIB_SHORT_CODE === 'ALLOCATED_BU') {
              new_row.Line_data_occ[i].ATTRIB_VALUE = this.componentSelected.ME_KEY;
              new_row.Line_data_occ[i].DISPLAY_TEXT = this.componentSelected.BSLA_DETAILS;
            }
            
            new_row.Line_data_occ[i].ALLOCATED_ME_KEY = this.componentSelected.ME_KEY;
            new_row.Line_data_occ[i].OCCURRENCE = occurrance;
            new_row.Line_data_occ[i].IS_CHANGED_FLAG = "Y";
          }
          
          this.cpFormData[x].Line_data.push(new_row);
        }
      }
    }
    
    this.putTotalLineAmounts();
    this.cleanFormData();
    this.editMode = false;
    this.editMe = false;
    this.cdr.detectChanges();
  }

  removeExistingRecord(Occurrence: any, Trans_details_key: any, index: number, parentIndex: number) {
    if (Trans_details_key == null || Trans_details_key === undefined || Trans_details_key === '' || Trans_details_key.length <= 0) {
      this.cpFormData[parentIndex].Line_data.splice(index, 1);
    } else {
      for (let i = 0; i < this.cpFormData[parentIndex].Line_data[index].Line_data_occ.length; i++) {
        this.cpFormData[parentIndex].Line_data[index].Line_data_occ[i].IS_CHANGED_FLAG = 'D';
      }
      this.cpFormData[parentIndex].DELETE_ROWS.push(this.cpFormData[parentIndex].Line_data[index]);
      this.cpFormData[parentIndex].Line_data.splice(index, 1);
    }
    this.putTotalLineAmounts();
    this.cdr.detectChanges();
  }

  editExistingRecord(Occurrence: any, Trans_details_key: any, index: number, parentIndex: number, lock_status: number) {
    this.edit_record_index = index;
    this.edit_record_parentindex = parentIndex;
    this.edit_record_occurrence = Occurrence;
    if (this.cpFormData[parentIndex].ROW_TYPE === 'CH') {
      for (let i = 0; i < this.cpFormData[parentIndex].Line_data.length; i++) {
        if (i === index && this.cpFormData[parentIndex].Line_data[i].Occurrence === Occurrence) {
          for (let j = 0; j < this.cpFormData[parentIndex].Line_data[i].Line_data_occ.length; j++) {
            if (this.cpFormData[parentIndex].Line_data[i].Line_data_occ[j].ATTRIB_SHORT_CODE === 'ALLOCATED_AMT') {
              this.allocated_amount = this.cpFormData[parentIndex].Line_data[i].Line_data_occ[j].ATTRIB_VALUE;
            } else if (this.cpFormData[parentIndex].Line_data[i].Line_data_occ[j].ATTRIB_SHORT_CODE === 'ALLOCATED_BU') {
              this.selectedBSLA = this.cpFormData[parentIndex].Line_data[i].Line_data_occ[j].DISPLAY_TEXT;
              
              const selectedbslaIndex = this.componentDetails.findIndex(x => x.BSLA_DETAILS === this.selectedBSLA);
              if (selectedbslaIndex !== -1) {
                this.componentSelected = this.componentDetails[selectedbslaIndex];
              }
            }
          }
        }
      }
    }
    if (lock_status === 1) {
      this.editMe = true;
      this.getComponentDetails();
    } else {
      this.editMode = true;
    }
    this.cdr.detectChanges();
  }

  save() {
    this.finalSaveData = [];
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }
    
    this.cpFormFinalData = _.cloneDeep(this.cpFormData);
    if (_.isEqual(this.cpFormData, this.cpFormOriginalData)) {
      this.cancel();
    } else {
      this.manipulateData();
      
      this.clob_settings = [{
        sso_id: this.logged_in_user,
        screen_key: this.globalService?.screen_key
      }];
      this.sendsaveDetails(this.finalSaveData, this.clob_settings, this.form_name, this.schedule_name);
    }
  }

  sendsaveDetails(final_data: any[], clob_settings: any[], form: string, schedule: string) {
    const message = "IC Allocation is successfully updated.";
    this.crudLoading = true;
    this.isSaveClicked = true;
    
    this.icDiagnosticsService.saveICForm(
      this.GENERAL_CONFIG.base_url,
      final_data,
      clob_settings,
      form,
      schedule,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (null != data.errorMessage && data.errorMessage !== "null") {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        console.error('Error saving IC Form:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  manipulateData() {
    for (let i = 0; i < this.cpFormFinalData.length; i++) {
      if (this.cpFormFinalData[i].ROW_TYPE === 'CH') {
        for (let j = 0; j < this.cpFormFinalData[i].Line_data.length; j++) {
          if (this.cpFormFinalData[i].Line_data[j].Line_Type === 'DATA_ROW') {
            for (let k = 0; k < this.cpFormFinalData[i].Line_data[j].Line_data_occ.length; k++) {
              this.finalSaveData.push(this.cpFormFinalData[i].Line_data[j].Line_data_occ[k]);
            }
          }
        }
        if (this.cpFormFinalData[i].DELETE_ROWS.length != null &&
            this.cpFormFinalData[i].DELETE_ROWS.length !== undefined &&
            this.cpFormFinalData[i].DELETE_ROWS.length > 0) {
          for (let m = 0; m < this.cpFormFinalData[i].DELETE_ROWS.length; m++) {
            if (this.cpFormFinalData[i].DELETE_ROWS[m].Line_Type === 'DATA_ROW') {
              for (let n = 0; n < this.cpFormFinalData[i].DELETE_ROWS[m].Line_data_occ.length; n++) {
                this.finalSaveData.push(this.cpFormFinalData[i].DELETE_ROWS[m].Line_data_occ[n]);
              }
            }
          }
        }
      } else if (this.cpFormFinalData[i].ROW_TYPE === 'TH') {
        for (let j = 0; j < this.cpFormFinalData[i].Line_data.length; j++) {
          this.finalSaveData.push(this.cpFormFinalData[i].Line_data[j]);
        }
      }
    }
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  checkAllDataLoaded() {
    // Only hide main loader when ALL required data is loaded
    const allLoaded = Object.values(this.dataLoadingStates).every(state => state);
    if (allLoaded) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }
}

